/**
 *
 * @component 个人中心/项目管理/协作项目详情/施工进度计划/关联施工图弹框
 *
 **/

import {
    Tree,
    Checkbox,
    Space,
    Button,
    Dropdown,
    Menu,
    Spin,
    Empty,
} from 'antd';
import BaseModal from '&/components/BaseModal';
import { memo } from 'react';
import './PlanTreeModal.scss';
import { queryDrawingTree } from '@/api/personCenter/projectSpace1.4';
import { useDidUpdate } from '&/utils/customHooks';

function PlanTreeModal(props) {
    const {
        visible,
        setVisible,
        onSelected,
        projectId,
        initValues = [],
    } = props;
    const [treeData, setTreeData] = useState([]);

    useEffect(() => {
        visible && getTree();
    }, [visible]);

    const [loading, setloading] = useState(false);
    const [checkedKeys, setCheckedKeys] = useState([]); //  tree 受控选中的node的key
    const checkNodes = useRef([]);
    const nodeAmount = useRef(0); //  数据平铺后的节点总数

    const getTree = async () => {
        setloading(true);
        try {
            const { data } = await queryDrawingTree({ projectId });
            const TREE = data.filter(e => {
                return e.isLeaf || !!e.children.length;
            });
            setTreeData(TREE);
            let arr = TREE.map(e => e.key);
            setExpandedKeys(arr); // 展开所有根节点
            nodeAmount.current = 0;
            TREE.forEach(el => {
                if (el.isLeaf) {
                    nodeAmount.current++;
                } else {
                    el.children.forEach(cel => {
                        nodeAmount.current++;
                        arr.push(cel.key);
                    });
                }
            });
            // 回显施工图
            if (initValues.length) {
                checkNodes.current = initValues.filter(e =>
                    arr.includes(e.key)
                );
                setCheckedKeys(checkNodes.current.map(e => e.key));
            } else {
                checkNodes.current = [];
                setCheckedKeys([]);
            }
        } finally {
            setloading(false);
        }
    };

    const [checkAmount, setAmount] = useState(0); // 选中图的个数(除过文件夹)
    const [checkAll, setCheckAll] = useState(false); //  全选check
    const [indeterminate, setIndeterminate] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState([]); // 展开的组

    useDidUpdate(() => {
        const isAll =
            nodeAmount.current === checkNodes.current.length &&
            nodeAmount.current > 0;
        if (!checkedKeys.length) {
            let m = !!checkedKeys.length;
            setIndeterminate(m);
        } else {
            let m = !isAll && !!checkNodes.current.length;
            setIndeterminate(m);
        }
        setAmount(checkNodes.current.length);
        setCheckAll(isAll);
    }, [checkedKeys]);

    const onCheck = (keys, { checkedNodes }) => {
        setCheckedKeys(keys);
        const selectedNodes = checkedNodes.filter(e => e.isLeaf);
        checkNodes.current = selectedNodes;
    };

    const onExpand = keys => {
        setExpandedKeys(keys);
    };

    const onCheckAllChange = e => {
        let keys = [];
        let nodes = [];
        if (e.target.checked) {
            treeData.forEach(el => {
                keys.push(el.key);
                if (el.isLeaf) {
                    nodes.push(el);
                } else {
                    el.children.forEach(cel => {
                        keys.push(cel.key);
                        nodes.push(cel);
                    });
                }
            });
        }
        checkNodes.current = nodes;
        setCheckedKeys(keys);
    };

    //  关闭关联施工图弹窗
    const closePlanModal = () => {
        setVisible(false);
    };

    const confirmSelect = () => {
        onSelected(checkedKeys, checkNodes.current);
        closePlanModal();
    };

    const menuClick = ({ key }) => {
        switch (key) {
            case '1':
                setExpandedKeys(treeData.map(e => e.key));
                break;
            case '2':
                setExpandedKeys([]);
                break;
            default:
                setCheckedKeys([]);
                break;
        }
    };

    const menu = (
        <Menu onClick={menuClick}>
            <Menu.Item key="1">{intl.get('projectSpace.expandAll')}</Menu.Item>
            <Menu.Item key="2">{intl.get('projectSpace.foldAll')}</Menu.Item>
            <Menu.Item key="3">{intl.get('projectSpace.cancelAll')}</Menu.Item>
        </Menu>
    );
    return (
        <BaseModal
            title={intl.get('projectSpace.drawingsAssociate')}
            width="21.25rem"
            visible={visible}
            onCancel={closePlanModal}
            footer={
                <Space>
                    <Button onClick={closePlanModal}>
                        {intl.get('base.cancel')}
                    </Button>
                    <Button type="primary" onClick={confirmSelect}>
                        {intl.get('base.confirm')}
                    </Button>
                </Space>
            }>
            <div className="plan-tree-modal">
                <Spin spinning={loading}>
                    {treeData.length ? (
                        <>
                            <header className="tree-header">
                                <Checkbox
                                    indeterminate={indeterminate}
                                    onChange={onCheckAllChange}
                                    checked={checkAll}>
                                    {intl.get('projectSpace.selectCount', {
                                        count: checkAmount,
                                    })}
                                </Checkbox>
                            </header>
                            <Dropdown overlay={menu} trigger={['contextMenu']}>
                                <Tree
                                    checkable
                                    titleRender={nodeData => {
                                        return (
                                            <span title={nodeData.name}>
                                                {nodeData.name}
                                            </span>
                                        );
                                    }}
                                    height={400}
                                    style={{
                                        minHeight: 400,
                                    }}
                                    defaultExpandAll
                                    onCheck={onCheck}
                                    expandedKeys={expandedKeys}
                                    onExpand={onExpand}
                                    blockNode
                                    selectable={false}
                                    showIcon
                                    icon={props => {
                                        return props.data.children ? (
                                            <i className="icon-Docment-PDF filled" />
                                        ) : (
                                            <i className="filled icon-Floor-Plan" />
                                        );
                                    }}
                                    checkedKeys={checkedKeys}
                                    treeData={treeData}
                                />
                            </Dropdown>
                        </>
                    ) : (
                        <Empty
                            image={
                                require('@/assets/img/userCenter1.4/emptyydata-resource.png')
                                    .default
                            }
                            imageStyle={{
                                height: 140,
                            }}
                            style={{
                                margin: '100px 0',
                            }}
                            description={
                                <>
                                    <div className="message">
                                        {intl.get('projectSpace.noDrawing')}
                                    </div>
                                </>
                            }>
                            {/* <div>立即上传</div> */}
                        </Empty>
                    )}
                </Spin>
            </div>
        </BaseModal>
    );
}

export default memo(PlanTreeModal);
